/**
 * Admin → Training (Task #18).
 *
 * Modules list with status / required flag / lesson + question counts and
 * a "New module" action. Sibling link points at the per-user roster view
 * delivered in Task #17.
 */
import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ModuleAdminDetail, ModuleAdminSummary, ScormPackage } from '@xsc/shared';
import { formatBytes, manifestLabel } from '../components/training/scorm-package-list.js';
import { api, ApiError } from '../lib/api-client.js';
import { Card, CardBody, CardHeader, CardTitle } from '../components/ui/card.js';
import { Button } from '../components/ui/button.js';
import { Badge } from '../components/ui/badge.js';
import { Input } from '../components/ui/input.js';
import { FormBanner, FormField } from '../components/ui/form.js';

type Filter = 'active' | 'inactive' | 'all';
type WindowDays = 30 | 60 | 90;

interface ExpiringCertInternal {
  cert_id: string;
  user_sub: string;
  user_email: string | null;
  user_name: string | null;
  certificate_ref: string;
  earned_at: string;
  expires_at: string;
  last_reminder_step: number;
  last_reminder_sent_at: string | null;
  opted_out_at: string | null;
}
interface ExpiringCertExternal {
  cert_id: string;
  module_id: string;
  module_title: string;
  contact_profile_id: string;
  contact_name: string | null;
  contact_email: string | null;
  certificate_ref: string;
  earned_at: string;
  expires_at: string;
  last_reminder_step: number;
  last_reminder_sent_at: string | null;
  opted_out_at: string | null;
}

export function AdminTrainingPage(): JSX.Element {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [filter, setFilter] = useState<Filter>('active');
  const [creating, setCreating] = useState(false);
  const [slug, setSlug] = useState('');
  const [title, setTitle] = useState('');
  const [createErr, setCreateErr] = useState<string | null>(null);

  const q = useQuery({
    queryKey: ['admin', 'training', 'modules'],
    queryFn: () => api.get<{ modules: ModuleAdminSummary[] }>('/v1/admin/training/modules'),
  });

  const createMut = useMutation({
    mutationFn: async (): Promise<ModuleAdminDetail> =>
      api.post<ModuleAdminDetail>('/v1/admin/training/modules', { slug, title }),
    onSuccess: (detail) => {
      qc.invalidateQueries({ queryKey: ['admin', 'training', 'modules'] });
      setCreating(false);
      setSlug('');
      setTitle('');
      navigate(`/admin/training/${detail.module.id}`);
    },
    onError: (err) => setCreateErr(extractDetail(err)),
  });

  const onCreate = (e: FormEvent) => {
    e.preventDefault();
    setCreateErr(null);
    createMut.mutate();
  };

  const modules = (q.data?.modules ?? []).filter((m) =>
    filter === 'all' ? true : filter === 'active' ? m.is_active : !m.is_active,
  );

  return (
    <div className="space-y-4">
      <header className="flex items-baseline justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-semibold">Training modules</h1>
          <p className="text-sm text-ink-muted">
            Author the catalog learners see at <Link className="underline" to="/training">/training</Link>.
            View per-user completion in the{' '}
            <Link className="underline" to="/admin/training/roster">roster</Link>.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded border border-border overflow-hidden text-sm">
            {(['active', 'inactive', 'all'] as Filter[]).map((f) => (
              <button
                key={f}
                type="button"
                onClick={() => setFilter(f)}
                className={`px-3 py-1.5 capitalize ${filter === f ? 'bg-accent text-accent-fg' : 'bg-surface'}`}
              >
                {f}
              </button>
            ))}
          </div>
          <Button onClick={() => setCreating((v) => !v)}>
            {creating ? 'Cancel' : 'New module'}
          </Button>
        </div>
      </header>

      {creating && (
        <Card>
          <CardHeader><CardTitle>New module</CardTitle></CardHeader>
          <CardBody>
            <form onSubmit={onCreate} className="space-y-3">
              {createErr && <FormBanner message={createErr} />}
              <FormField label="Title" required>
                {(id) => (
                  <Input id={id} required value={title} onChange={(e) => setTitle(e.target.value)} />
                )}
              </FormField>
              <FormField label="Slug" required hint="Lowercase letters, digits, and dashes only.">
                {(id) => (
                  <Input
                    id={id}
                    required
                    value={slug}
                    onChange={(e) => setSlug(e.target.value)}
                    pattern="[a-z0-9][a-z0-9\-]*"
                  />
                )}
              </FormField>
              <div className="flex justify-end">
                <Button type="submit" disabled={createMut.isPending}>
                  {createMut.isPending ? 'Creating…' : 'Create module'}
                </Button>
              </div>
            </form>
          </CardBody>
        </Card>
      )}

      <Card>
        <CardHeader><CardTitle>Catalog</CardTitle></CardHeader>
        <CardBody className="overflow-x-auto">
          {q.isLoading ? (
            <p className="text-sm text-ink-muted">Loading…</p>
          ) : q.isError ? (
            <p className="text-sm text-danger">Failed to load modules.</p>
          ) : modules.length === 0 ? (
            <p className="text-sm text-ink-muted">No modules in this view.</p>
          ) : (
            <table className="w-full text-sm border-collapse">
              <thead className="text-left text-xs uppercase text-ink-muted">
                <tr className="border-b border-border">
                  <th className="py-2 pr-4">Title</th>
                  <th className="py-2 pr-4">Slug</th>
                  <th className="py-2 pr-4">Status</th>
                  <th className="py-2 pr-4">Required</th>
                  <th className="py-2 pr-4">Lessons</th>
                  <th className="py-2 pr-4">Questions</th>
                  <th className="py-2 pr-4">Pass</th>
                  <th className="py-2 pr-4">Sort</th>
                </tr>
              </thead>
              <tbody>
                {modules.map((m) => (
                  <tr key={m.id} className="border-b border-border hover:bg-surface-alt">
                    <td className="py-2 pr-4">
                      <Link to={`/admin/training/${m.id}`} className="text-accent underline">
                        {m.title}
                      </Link>
                    </td>
                    <td className="py-2 pr-4 font-mono text-xs">{m.slug}</td>
                    <td className="py-2 pr-4">
                      {m.is_active ? (
                        <Badge tone="success">Active</Badge>
                      ) : (
                        <Badge tone="neutral">Inactive</Badge>
                      )}
                    </td>
                    <td className="py-2 pr-4">
                      {m.is_required ? <Badge tone="info">Required</Badge> : <span className="text-ink-muted">Optional</span>}
                    </td>
                    <td className="py-2 pr-4">{m.lesson_count}</td>
                    <td className="py-2 pr-4">{m.question_count}</td>
                    <td className="py-2 pr-4">{m.passing_score}%</td>
                    <td className="py-2 pr-4">{m.sort_order}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardBody>
      </Card>

      <OrphanedScormCard />

      <StorageOrphanedScormCard />

      <ExpiringSoonCard />
    </div>
  );
}

/** Orphaned SCORM packages: uploaded packages no lesson block references
 *  anymore (e.g. the block was removed in the editor). They only consume
 *  object storage, so surface them here with a delete action. */
function OrphanedScormCard(): JSX.Element {
  const qc = useQueryClient();
  const [actionErr, setActionErr] = useState<string | null>(null);
  const q = useQuery({
    queryKey: ['admin', 'scorm', 'orphaned'],
    queryFn: () =>
      api.get<{ packages: ScormPackage[] }>('/v1/admin/training/scorm-packages/orphaned'),
  });

  const del = useMutation({
    mutationFn: (packageId: string) =>
      api.delete<{ ok: true }>(
        `/v1/admin/training/scorm-packages/${encodeURIComponent(packageId)}`,
      ),
    onSuccess: () => {
      setActionErr(null);
      void qc.invalidateQueries({ queryKey: ['admin', 'scorm'] });
    },
    onError: (err) => setActionErr(extractDetail(err)),
  });

  const packages = q.data?.packages ?? [];
  // Hide the card entirely when clean — this is a maintenance surface.
  if (!q.isLoading && !q.isError && packages.length === 0) return <></>;

  return (
    <Card>
      <CardHeader><CardTitle>Orphaned SCORM packages</CardTitle></CardHeader>
      <CardBody className="overflow-x-auto space-y-3">
        {actionErr && <FormBanner message={actionErr} />}
        {q.isLoading ? (
          <p className="text-sm text-ink-muted">Loading…</p>
        ) : q.isError ? (
          <p className="text-sm text-danger">Failed to load orphaned packages.</p>
        ) : (
          <>
            <p className="text-sm text-ink-muted">
              These packages are no longer referenced by any lesson block and
              only consume object storage. Deleting them cannot be undone.
            </p>
            <table className="w-full text-sm border-collapse">
              <thead className="text-left text-xs uppercase text-ink-muted">
                <tr className="border-b border-border">
                  <th className="py-2 pr-4">Title</th>
                  <th className="py-2 pr-4">Version</th>
                  <th className="py-2 pr-4">Size</th>
                  <th className="py-2 pr-4">Uploaded</th>
                  <th className="py-2 pr-4"></th>
                </tr>
              </thead>
              <tbody>
                {packages.map((p) => (
                  <tr key={p.id} className="border-b border-border hover:bg-surface-alt">
                    <td className="py-2 pr-4">{p.title ?? <span className="font-mono text-xs">{p.id}</span>}</td>
                    <td className="py-2 pr-4"><Badge tone="neutral">{manifestLabel(p.manifest_version)}</Badge></td>
                    <td className="py-2 pr-4">{formatBytes(p.total_bytes)}</td>
                    <td className="py-2 pr-4 text-ink-muted">{new Date(p.uploaded_at).toLocaleString()}</td>
                    <td className="py-2 pr-4">
                      <button
                        type="button"
                        className="text-xs px-2 py-1 rounded border border-border text-danger"
                        disabled={del.isPending}
                        onClick={() => {
                          if (confirm(`Delete orphaned SCORM package "${p.title ?? p.id}"?`))
                            del.mutate(p.id);
                        }}
                      >
                        {del.isPending && del.variables === p.id ? 'Deleting…' : 'Delete'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </CardBody>
    </Card>
  );
}

interface StorageOrphan {
  prefix: string;
  packageId: string;
}

/** Storage-level SCORM orphans: GCS prefixes that exist but have no DB row.
 *  These are left behind by extraction failures or incomplete deletes and
 *  silently consume storage. */
function StorageOrphanedScormCard(): JSX.Element {
  const qc = useQueryClient();
  const [actionErr, setActionErr] = useState<string | null>(null);
  const q = useQuery({
    queryKey: ['admin', 'scorm', 'storage-orphaned'],
    queryFn: () =>
      api.get<{ orphans: StorageOrphan[] }>(
        '/v1/admin/training/scorm-packages/storage-orphaned',
      ),
  });

  const del = useMutation({
    mutationFn: (packageId: string) =>
      api.delete<{ ok: true; deleted: number }>(
        `/v1/admin/training/scorm-packages/storage-orphaned/${encodeURIComponent(packageId)}`,
      ),
    onSuccess: () => {
      setActionErr(null);
      void qc.invalidateQueries({ queryKey: ['admin', 'scorm'] });
    },
    onError: (err) => setActionErr(extractDetail(err)),
  });

  const orphans = q.data?.orphans ?? [];
  // Hide the card when clean — this is a maintenance surface.
  if (!q.isLoading && !q.isError && orphans.length === 0) return <></>;

  return (
    <Card>
      <CardHeader><CardTitle>Orphaned SCORM storage</CardTitle></CardHeader>
      <CardBody className="overflow-x-auto space-y-3">
        {actionErr && <FormBanner message={actionErr} />}
        {q.isLoading ? (
          <p className="text-sm text-ink-muted">Loading…</p>
        ) : q.isError ? (
          <p className="text-sm text-danger">Failed to load storage orphans.</p>
        ) : (
          <>
            <p className="text-sm text-ink-muted">
              These storage prefixes exist in object storage but have no
              matching database record — typically left behind by a failed
              extraction or interrupted delete. Removing them cannot be undone.
            </p>
            <table className="w-full text-sm border-collapse">
              <thead className="text-left text-xs uppercase text-ink-muted">
                <tr className="border-b border-border">
                  <th className="py-2 pr-4">Package ID</th>
                  <th className="py-2 pr-4">Storage prefix</th>
                  <th className="py-2 pr-4"></th>
                </tr>
              </thead>
              <tbody>
                {orphans.map((o) => (
                  <tr key={o.packageId} className="border-b border-border hover:bg-surface-alt">
                    <td className="py-2 pr-4 font-mono text-xs">{o.packageId}</td>
                    <td className="py-2 pr-4 font-mono text-xs text-ink-muted break-all">{o.prefix}</td>
                    <td className="py-2 pr-4">
                      <button
                        type="button"
                        className="text-xs px-2 py-1 rounded border border-border text-danger"
                        disabled={del.isPending}
                        onClick={() => {
                          if (confirm(`Delete orphaned storage for package "${o.packageId}"?`))
                            del.mutate(o.packageId);
                        }}
                      >
                        {del.isPending && del.variables === o.packageId ? 'Deleting…' : 'Delete'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </CardBody>
    </Card>
  );
}

function ExpiringSoonCard(): JSX.Element {
  const qc = useQueryClient();
  const [windowDays, setWindowDays] = useState<WindowDays>(30);
  const [actionErr, setActionErr] = useState<string | null>(null);
  const q = useQuery({
    queryKey: ['admin', 'training', 'expiring', windowDays],
    queryFn: () =>
      api.get<{ internal: ExpiringCertInternal[]; external: ExpiringCertExternal[] }>(
        `/v1/admin/training/expiring?within_days=${windowDays}`,
      ),
  });

  const resendMut = useMutation({
    mutationFn: async (args: { scope: 'internal' | 'external'; certId: string }) =>
      api.post<{ ok: true; tick: { sent: number; skipped: number; failed: number } }>(
        `/v1/admin/training/expiring/${args.scope}/${args.certId}/resend`,
        {},
      ),
    onSuccess: () => {
      setActionErr(null);
      qc.invalidateQueries({ queryKey: ['admin', 'training', 'expiring'] });
    },
    onError: (err) => setActionErr(extractDetail(err)),
  });

  const internal = q.data?.internal ?? [];
  const external = q.data?.external ?? [];
  const empty = internal.length === 0 && external.length === 0;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle>Expiring soon</CardTitle>
          <div className="flex rounded border border-border overflow-hidden text-sm">
            {([30, 60, 90] as WindowDays[]).map((d) => (
              <button
                key={d}
                type="button"
                onClick={() => setWindowDays(d)}
                className={`px-3 py-1.5 ${windowDays === d ? 'bg-accent text-accent-fg' : 'bg-surface'}`}
              >
                &lt; {d}d
              </button>
            ))}
          </div>
        </div>
      </CardHeader>
      <CardBody className="space-y-4">
        {actionErr && <FormBanner message={actionErr} />}
        {q.isLoading ? (
          <p className="text-sm text-ink-muted">Loading…</p>
        ) : q.isError ? (
          <p className="text-sm text-danger">Failed to load expiring certificates.</p>
        ) : empty ? (
          <p className="text-sm text-ink-muted">
            No certificates expire within the next {windowDays} days.
          </p>
        ) : (
          <>
            {internal.length > 0 && (
              <ExpiringTable<ExpiringCertInternal>
                title="Internal staff"
                rows={internal}
                renderRecipient={(r) => r.user_name ?? r.user_email ?? r.user_sub}
                renderCourse={() => 'Required curriculum'}
                onResend={(r) =>
                  resendMut.mutate({ scope: 'internal', certId: r.cert_id })
                }
                pendingId={resendMut.isPending ? (resendMut.variables?.certId ?? null) : null}
              />
            )}
            {external.length > 0 && (
              <ExpiringTable<ExpiringCertExternal>
                title="External learners"
                rows={external}
                renderRecipient={(r) => r.contact_name ?? r.contact_email ?? r.contact_profile_id}
                renderCourse={(r) => r.module_title}
                onResend={(r) =>
                  resendMut.mutate({ scope: 'external', certId: r.cert_id })
                }
                pendingId={resendMut.isPending ? (resendMut.variables?.certId ?? null) : null}
              />
            )}
          </>
        )}
      </CardBody>
    </Card>
  );
}

interface ExpiringRow {
  cert_id: string;
  expires_at: string;
  last_reminder_step: number;
  last_reminder_sent_at: string | null;
  opted_out_at: string | null;
}
function ExpiringTable<T extends ExpiringRow>(props: {
  title: string;
  rows: T[];
  renderRecipient: (r: T) => string;
  renderCourse: (r: T) => string;
  onResend: (r: T) => void;
  pendingId: string | null;
}): JSX.Element {
  return (
    <div>
      <h3 className="text-sm font-medium mb-2">{props.title}</h3>
      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead className="text-left text-xs uppercase text-ink-muted">
            <tr className="border-b border-border">
              <th className="py-2 pr-4">Recipient</th>
              <th className="py-2 pr-4">Course</th>
              <th className="py-2 pr-4">Expires</th>
              <th className="py-2 pr-4">Reminders sent</th>
              <th className="py-2 pr-4">Last reminder</th>
              <th className="py-2 pr-4"></th>
            </tr>
          </thead>
          <tbody>
            {props.rows.map((r) => (
              <tr key={r.cert_id} className="border-b border-border hover:bg-surface-alt">
                <td className="py-2 pr-4">{props.renderRecipient(r)}</td>
                <td className="py-2 pr-4">{props.renderCourse(r)}</td>
                <td className="py-2 pr-4">
                  {new Date(r.expires_at).toLocaleDateString()}
                  {r.opted_out_at && (
                    <span className="ml-2"><Badge tone="neutral">Opted out</Badge></span>
                  )}
                </td>
                <td className="py-2 pr-4">{r.last_reminder_step} / 2</td>
                <td className="py-2 pr-4 text-ink-muted">
                  {r.last_reminder_sent_at
                    ? new Date(r.last_reminder_sent_at).toLocaleString()
                    : '—'}
                </td>
                <td className="py-2 pr-4">
                  <Button
                    type="button"
                    onClick={() => props.onResend(r)}
                    disabled={props.pendingId === r.cert_id}
                  >
                    {props.pendingId === r.cert_id ? 'Sending…' : 'Resend reminder'}
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function extractDetail(err: unknown): string {
  if (err instanceof ApiError && typeof err.body === 'object' && err.body && 'detail' in err.body) {
    const d = (err.body as { detail: unknown }).detail;
    if (typeof d === 'string') return d;
    return JSON.stringify(d);
  }
  return (err as Error).message;
}
